import { useCallback, useEffect, useRef } from 'react';
import { log } from '@/lib/logger';

/**
 * Clerk's PricingTable defaults to annual billing and exposes no prop to
 * override it (PricingTableProps in @clerk/shared and Clerk's docs both
 * re-verified 2026-07-03: only appearance/checkoutProps/collapseFeatures/
 * ctaPosition/fallback/for/highlightedPlan/newSubscriptionRedirectUrl).
 *
 * This hook returns a CALLBACK REF for the container that wraps
 * <PricingTable />. When Clerk renders its billing-period switches —
 * <input type="checkbox" role="switch" checked> with no aria-checked
 * (verified live 2026-07-03) — it clicks ONE switch at a time until none
 * read annual, then permanently stops so it never overrides a later manual
 * choice.
 *
 * Why a callback ref and not a RefObject effect: BillingPage mounts the
 * container only after useBillingInfo resolves, so a mount-time effect that
 * reads ref.current runs too early, bails, and never re-runs — the bug that
 * shipped annual-by-default on /billing. See debug_log 2026-07-03.
 *
 * Why click one switch per pass: the billing period is ONE shared state
 * across plan cards (clicking any switch flips every card). Clicking all N
 * matched switches toggles that state N times and lands back on annual for
 * even N.
 */
const MAX_FLIP_CLICKS = 4;
const OBSERVE_TIMEOUT_MS = 15_000;
const RECHECK_DELAY_MS = 50;

// What Clerk renders today, plus aria-checked switches in case Clerk fixes
// its switch semantics later. Clicks toggle both variants.
const ANNUAL_SWITCH_SELECTOR =
  'input[type="checkbox"][role="switch"]:checked, input[type="checkbox"]:checked, [role="switch"][aria-checked="true"]';

export function useForceMonthlyDefault(): (node: HTMLElement | null) => void {
  // One-shot per component instance: once monthly is confirmed, re-attached
  // containers (re-renders, refetch remounts) must not re-force the period.
  const doneRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Detach the observer/timers when the owning component unmounts.
  useEffect(() => () => cleanupRef.current?.(), []);

  return useCallback((node: HTMLElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node || doneRef.current) return;

    let clicks = 0;
    let recheck: ReturnType<typeof setTimeout> | undefined;

    const detach = () => {
      observer.disconnect();
      clearTimeout(timeout);
      clearTimeout(recheck);
      cleanupRef.current = null;
    };

    const tryFlip = () => {
      if (doneRef.current) return;
      const target = node.querySelector<HTMLElement>(ANNUAL_SWITCH_SELECTOR);

      if (!target) {
        if (clicks > 0) {
          // Every switch reads monthly after our click(s) — done for good.
          doneRef.current = true;
          detach();
          log.info(`[useForceMonthlyDefault] Defaulted to monthly (${clicks} click(s))`);
        }
        // clicks === 0: Clerk hasn't rendered yet (or already shows
        // monthly); keep observing until the timeout.
        return;
      }

      if (clicks >= MAX_FLIP_CLICKS) {
        // Still annual after the budget — stop rather than fight the UI.
        detach();
        log.warn('[useForceMonthlyDefault] Gave up flipping to monthly (click budget spent)');
        return;
      }

      clicks += 1;
      target.click();
      // The click changes the checked PROPERTY only, which MutationObserver
      // cannot see — re-check on a short timer instead.
      clearTimeout(recheck);
      recheck = setTimeout(tryFlip, RECHECK_DELAY_MS);
    };

    const observer = new MutationObserver(tryFlip);
    observer.observe(node, { childList: true, subtree: true });
    const timeout = setTimeout(detach, OBSERVE_TIMEOUT_MS);
    cleanupRef.current = detach;

    // Clerk may already be in the DOM (e.g. warm remount).
    tryFlip();
  }, []);
}
